<script>
import { mapGetters } from 'vuex';
import { shouldBeUrl } from 'shared/helpers/Validators';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import Avatar from 'next/avatar/Avatar.vue';
import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import SettingsSection from '../../../../components/SettingsSection.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import FacebookReauthorize from './facebook/Reauthorize.vue';
import InstagramReauthorize from './channels/instagram/Reauthorize.vue';
import TiktokReauthorize from './channels/tiktok/Reauthorize.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import MicrosoftReauthorize from './channels/microsoft/Reauthorize.vue';
import GoogleReauthorize from './channels/google/Reauthorize.vue';
import WhatsappReauthorize from './channels/whatsapp/Reauthorize.vue';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import PreChatFormSettings from './PreChatForm/Settings.vue';
import WeeklyAvailability from './components/WeeklyAvailability.vue';
import GreetingsEditor from 'shared/components/GreetingsEditor.vue';
import ConfigurationPage from './settingsPage/ConfigurationPage.vue';
import CustomerSatisfactionPage from './settingsPage/CustomerSatisfactionPage.vue';
import CollaboratorsPage from './settingsPage/CollaboratorsPage.vue';
import WorkflowsPage from './settingsPage/WorkflowsPage.vue';
import WidgetBuilder from './WidgetBuilder.vue';
import BotConfiguration from './components/BotConfiguration.vue';
import AccountHealth from './components/AccountHealth.vue';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import SenderNameExamplePreview from './components/SenderNameExamplePreview.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import InboxLabelSelector from './components/InboxLabelSelector.vue';

export default {
  components: {
    BotConfiguration,
    CollaboratorsPage,
    ConfigurationPage,
    CustomerSatisfactionPage,
    FacebookReauthorize,
    GreetingsEditor,
    PreChatFormSettings,
    SettingIntroBanner,
    SettingsSection,
    WeeklyAvailability,
    WidgetBuilder,
    SenderNameExamplePreview,
    MicrosoftReauthorize,
    GoogleReauthorize,
    NextButton,
    InstagramReauthorize,
    TiktokReauthorize,
    WhatsappReauthorize,
    DuplicateInboxBanner,
    Editor,
    Avatar,
    AccountHealth,
    InboxLabelSelector,
    WorkflowsPage,
  },
  mixins: [inboxMixin],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      avatarFile: null,
      avatarUrl: '',
      greetingEnabled: true,
      greetingMessage: '',
      emailCollectEnabled: false,
      senderNameType: 'friendly',
      businessName: '',
      locktoSingleConversation: false,
      enableAutoTicketCreation: false,
      allowMessagesAfterResolved: true,
      continuityViaEmail: true,
      selectedInboxName: '',
      channelWebsiteUrl: '',
      webhookUrl: '',
      channelWelcomeTitle: '',
      channelWelcomeTagline: '',
      selectedFeatureFlags: [],
      replyTime: '',
      selectedTabIndex: 0,
      selectedPortalSlug: '',
      showBusinessNameInput: false,
      healthData: null,
      isLoadingHealth: false,
      healthError: null,
      selectedLabels: [],
      selectedLabel: '',
      selectedTeamId: null,
      selectedBoardId: null,
      teamValidationError: false,
      enableAutoPositiveAutomation: false,
      enableNegativeAutomation: false,
      autoDmMessagePositive: '',
      autoDmMessageNegative: '',
      autoCommentMessageNegative: '',
      autoHideCommentOnNegative: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'inboxes/getUIFlags',
      portals: 'portals/allPortals',
      teams: 'teams/getTeams',
    }),
    selectedBoard() {
      if (!this.selectedBoardId) return null;
      return this.$store.getters['boards/getBoard'];
    },
    selectedTeam() {
      if (!this.selectedTeamId) return null;
      return this.teams.find(team => team.id === this.selectedTeamId);
    },
    autoTicketSummary() {
      if (!this.enableAutoTicketCreation || !this.selectedTeam) {
        return '';
      }
      const teamName = this.selectedTeam.name.toLowerCase();
      const boardName = this.selectedBoard?.name?.toLowerCase() || teamName;
      return `Tickets de e-mail serão automaticamente criados no time "${teamName}" e adicionados à primeira coluna do quadro "${boardName}"`;
    },
    selectedTabKey() {
      return this.tabs[this.selectedTabIndex]?.key;
    },
    shouldShowWhatsAppConfiguration() {
      return this.isAWhatsAppCloudChannel;
    },
    whatsAppAPIProviderName() {
      if (this.isAWhatsAppCloudChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD');
      }
      if (this.is360DialogWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.360_DIALOG');
      }
      if (this.isATwilioWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO');
      }
      return '';
    },
    tabs() {
      let visibleToAllChannelTabs = [
        {
          key: 'inbox-settings',
          name: this.$t('INBOX_MGMT.TABS.SETTINGS'),
        },
        {
          key: 'collaborators',
          name: this.$t('INBOX_MGMT.TABS.COLLABORATORS'),
        },
        {
          key: 'workflows',
          name: this.$t('INBOX_MGMT.TABS.WORKFLOWS'),
        },
      ];

      if (!this.isAVoiceChannel) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'business-hours',
            name: this.$t('INBOX_MGMT.TABS.BUSINESS_HOURS'),
          },
          {
            key: 'csat',
            name: this.$t('INBOX_MGMT.TABS.CSAT'),
          },
        ];
      }

      if (this.isAWebWidgetInbox) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'pre-chat-form',
            name: this.$t('INBOX_MGMT.TABS.PRE_CHAT_FORM'),
          },
          {
            key: 'widget-builder',
            name: this.$t('INBOX_MGMT.TABS.WIDGET_BUILDER'),
          },
        ];
      }

      if (
        this.isATwilioChannel ||
        this.isALineChannel ||
        this.isAPIInbox ||
        this.isAVoiceChannel ||
        (this.isAnEmailChannel && !this.inbox.provider) ||
        this.shouldShowWhatsAppConfiguration ||
        this.isAWebWidgetInbox
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'configuration',
            name: this.$t('INBOX_MGMT.TABS.CONFIGURATION'),
          },
        ];
      }

      if (
        this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.AGENT_BOTS)
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'bot-configuration',
            name: this.$t('INBOX_MGMT.TABS.BOT_CONFIGURATION'),
          },
        ];
      }
      if (this.shouldShowWhatsAppConfiguration) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'whatsapp-health',
            name: this.$t('INBOX_MGMT.TABS.ACCOUNT_HEALTH'),
          },
        ];
      }

      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'auto-messages',
            name: this.$t('INBOX_MGMT.TABS.AUTO_MESSAGES'),
          },
        ];
      }

      return visibleToAllChannelTabs;
    },
    currentInboxId() {
      return this.$route.params.inboxId;
    },
    inbox() {
      const inbox = this.$store.getters['inboxes/getInbox'](
        this.currentInboxId
      );
      return inbox || {};
    },
    inboxIcon() {
      const { medium, channel_type: type } = this.inbox;
      return getInboxIconByType(type, medium);
    },
    inboxName() {
      if (this.isATwilioSMSChannel || this.isATwilioWhatsAppChannel) {
        return `${this.inbox.name} (${
          this.inbox.messaging_service_sid || this.inbox.phone_number
        })`;
      }
      if (this.isAWhatsAppChannel) {
        return `${this.inbox.name} (${this.inbox.phone_number})`;
      }
      if (this.isAnEmailChannel) {
        return `${this.inbox.name} (${this.inbox.email})`;
      }
      return this.inbox.name;
    },
    canLocktoSingleConversation() {
      return (
        this.isASmsInbox ||
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAPIInbox ||
        this.isATelegramChannel
      );
    },
    inboxNameLabel() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.LABEL');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.LABEL');
    },
    inboxNamePlaceHolder() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.PLACEHOLDER');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.PLACEHOLDER');
    },
    textAreaChannels() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return true;
      return false;
    },
    instagramUnauthorized() {
      return this.isAnInstagramChannel && this.inbox.reauthorization_required;
    },
    tiktokUnauthorized() {
      return this.isATiktokChannel && this.inbox.reauthorization_required;
    },
    // Check if a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return this.inbox.channel_type === INBOX_TYPES.FB && instagramInbox;
    },
    microsoftUnauthorized() {
      return this.isAMicrosoftInbox && this.inbox.reauthorization_required;
    },
    facebookUnauthorized() {
      return this.isAFacebookInbox && this.inbox.reauthorization_required;
    },
    googleUnauthorized() {
      const isLegacyInbox = ['imap.gmail.com', 'imap.google.com'].includes(
        this.inbox.imap_address
      );

      return (
        (this.isAGoogleInbox || isLegacyInbox) &&
        this.inbox.reauthorization_required
      );
    },
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    whatsappUnauthorized() {
      return (
        this.isAWhatsAppCloudChannel &&
        this.isEmbeddedSignupWhatsApp &&
        this.inbox.reauthorization_required
      );
    },
    whatsappRegistrationIncomplete() {
      if (
        !this.healthData ||
        !this.isAWhatsAppCloudChannel ||
        !this.isEmbeddedSignupWhatsApp
      ) {
        return false;
      }

      return (
        this.healthData.platform_type === 'NOT_APPLICABLE' ||
        this.healthData.throughput?.level === 'NOT_APPLICABLE'
      );
    },
  },
  watch: {
    $route(to) {
      if (to.name === 'settings_inbox_show') {
        this.fetchInboxSettings();
      }
    },
    inbox: {
      handler(newInbox) {
        this.fetchHealthData();
        if (newInbox && newInbox.id) {
          const labelList = newInbox.label_list || newInbox.labelList || [];
          this.selectedLabels = Array.isArray(labelList) ? labelList : [];
          this.selectedLabel =
            this.selectedLabels.length > 0 ? this.selectedLabels[0] : '';
          this.selectedLabel =
            this.selectedLabels.length > 0 ? this.selectedLabels[0] : '';
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.fetchInboxSettings();
    this.fetchPortals();
    this.fetchHealthData();
  },
  methods: {
    getTextColor(hex = '#000000') {
      if (!hex) return '#000';
      const c = hex.replace('#', '');
      const rgb = parseInt(c, 16);
      const r = Math.floor(rgb / 65536);
      const g = Math.floor((rgb % 65536) / 256);
      const b = rgb % 256;
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.6 ? '#111827' : '#ffffff';
    },
    toggleLabel(label) {
      const title = typeof label === 'string' ? label : label.title;
      if (this.selectedLabels.includes(title)) {
        this.selectedLabels = [];
        this.selectedLabel = '';
      } else {
        this.selectedLabels = [title];
        this.selectedLabel = title;
      }
    },
    onLabelUpdate(labelTitle) {
      this.selectedLabel = labelTitle || '';
      this.selectedLabels = labelTitle ? [labelTitle] : [];
    },
    fetchPortals() {
      this.$store.dispatch('portals/index');
    },
    async fetchHealthData() {
      if (!this.inbox) return;

      if (!this.isAWhatsAppCloudChannel) {
        return;
      }

      try {
        this.isLoadingHealth = true;
        this.healthError = null;
        const response = await InboxHealthAPI.getHealthStatus(this.inbox.id);
        this.healthData = response.data;
      } catch (error) {
        this.healthError = error.message || 'Failed to fetch health data';
      } finally {
        this.isLoadingHealth = false;
      }
    },
    handleFeatureFlag(e) {
      this.selectedFeatureFlags = this.toggleInput(
        this.selectedFeatureFlags,
        e.target.value
      );
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
    refreshAvatarUrlOnTabChange(index) {
      // Refresh avatar URL on tab change from inbox-settings and widget-builder tabs, to ensure real-time updates
      const tab = this.tabs[index];
      if (
        this.inbox &&
        tab &&
        ['inbox-settings', 'widget-builder'].includes(tab.key)
      ) {
        this.avatarUrl = this.inbox.avatar_url;
      }
    },
    onTabChange(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
      this.refreshAvatarUrlOnTabChange(selectedTabIndex);
      this.updateRouteWithoutRefresh(selectedTabIndex);
    },
    updateRouteWithoutRefresh(selectedTabIndex) {
      const tab = this.tabs[selectedTabIndex];
      if (!tab) return;

      const { accountId, inboxId } = this.$route.params;
      const baseUrl = `/app/accounts/${accountId}/settings/inboxes/${inboxId}`;

      // Append the tab key only if it's not the default.
      const newUrl =
        tab.key === 'inbox-settings' ? baseUrl : `${baseUrl}/${tab.key}`;
      // Update URL without triggering route watcher
      window.history.replaceState(null, '', newUrl);
    },
    setTabFromRouteParam() {
      const { tab: tabParam } = this.$route.params;
      if (!tabParam) return;
      const tabIndex = this.tabs.findIndex(tab => tab.key === tabParam);

      this.selectedTabIndex = tabIndex === -1 ? 0 : tabIndex;
    },
    fetchInboxSettings() {
      this.selectedAgents = [];
      this.$store.dispatch('agents/get');
      this.$store.dispatch('teams/get');
      this.$store.dispatch('labels/get');
      this.$store.dispatch('inboxes/get').then(() => {
        this.avatarUrl = this.inbox.avatar_url;
        this.selectedInboxName = this.inbox.name;
        this.webhookUrl = this.inbox.webhook_url;
        this.greetingEnabled = this.inbox.greeting_enabled || false;
        this.greetingMessage = this.inbox.greeting_message || '';
        this.emailCollectEnabled = this.inbox.enable_email_collect;
        this.senderNameType = this.inbox.sender_name_type;
        this.businessName = this.inbox.business_name;
        this.allowMessagesAfterResolved =
          this.inbox.allow_messages_after_resolved;
        this.continuityViaEmail = this.inbox.continuity_via_email;
        this.channelWebsiteUrl = this.inbox.website_url;
        this.channelWelcomeTitle = this.inbox.welcome_title;
        this.channelWelcomeTagline = this.inbox.welcome_tagline || '';
        this.selectedFeatureFlags = this.inbox.selected_feature_flags || [];
        this.replyTime = this.inbox.reply_time;
        this.locktoSingleConversation = this.inbox.lock_to_single_conversation;
        this.enableAutoTicketCreation = this.inbox.enable_auto_ticket_creation;
        this.selectedTeamId = this.inbox.auto_ticket_team_id || null;
        this.selectedBoardId = this.inbox.auto_ticket_board_id || null;
        this.enableAutoPositiveAutomation =
          this.inbox.enable_auto_dm_positive || false;
        this.enableNegativeAutomation =
          this.inbox.enable_auto_dm_negative ||
          this.inbox.enable_auto_comment_on_classification ||
          false;
        this.autoDmMessagePositive = this.inbox.auto_dm_message_positive || '';
        this.autoDmMessageNegative = this.inbox.auto_dm_message_negative || '';
        this.autoCommentMessageNegative =
          this.inbox.auto_comment_message_negative || '';
        this.autoHideCommentOnNegative =
          this.inbox.auto_hide_comment_on_negative || false;
        this.selectedPortalSlug = this.inbox.help_center
          ? this.inbox.help_center.slug
          : '';

        // Set initial tab after inbox data is loaded
        this.setTabFromRouteParam();

        const labelList = this.inbox.label_list || this.inbox.labelList || [];
        this.selectedLabels = Array.isArray(labelList) ? labelList : [];
        this.selectedLabel =
          this.selectedLabels.length > 0 ? this.selectedLabels[0] : '';

        if (this.selectedTeamId) {
          this.syncBoardForTeam(this.selectedTeamId);
        }
      });
    },

    async updateInbox() {
      // Validate that team is selected when auto ticket creation is enabled
      if (this.enableAutoTicketCreation && !this.selectedTeamId) {
        this.teamValidationError = true;
        useAlert(
          'Por favor, selecione um time para criar tickets automáticos.'
        );
        return;
      }

      this.teamValidationError = false;

      try {
        const payload = {
          id: this.currentInboxId,
          name: this.selectedInboxName?.trim(),
          enable_email_collect: this.emailCollectEnabled,
          allow_messages_after_resolved: this.allowMessagesAfterResolved,
          greeting_enabled: this.greetingEnabled,
          labels: this.selectedLabel ? [this.selectedLabel] : [],
          greeting_message: this.greetingMessage || '',
          portal_id: this.selectedPortalSlug
            ? this.portals.find(
                portal => portal.slug === this.selectedPortalSlug
              ).id
            : null,
          lock_to_single_conversation: this.locktoSingleConversation,
          enable_auto_ticket_creation: this.enableAutoTicketCreation,
          enable_auto_dm_positive: this.enableAutoPositiveAutomation,
          enable_auto_dm_negative: this.enableNegativeAutomation,
          enable_auto_comment_on_classification: this.enableNegativeAutomation,
          auto_dm_message_positive: this.autoDmMessagePositive || '',
          auto_dm_message_negative: this.autoDmMessageNegative || '',
          auto_comment_message_negative: this.autoCommentMessageNegative || '',
          auto_hide_comment_on_negative: this.autoHideCommentOnNegative,
          auto_ticket_team_id: this.enableAutoTicketCreation
            ? this.selectedTeamId
            : null,
          auto_ticket_board_id: this.enableAutoTicketCreation
            ? this.selectedBoardId
            : null,
          sender_name_type: this.senderNameType,
          business_name: this.businessName || null,
          channel: {
            widget_color: this.inbox.widget_color,
            website_url: this.channelWebsiteUrl,
            webhook_url: this.webhookUrl,
            welcome_title: this.channelWelcomeTitle || '',
            welcome_tagline: this.channelWelcomeTagline || '',
            selectedFeatureFlags: this.selectedFeatureFlags,
            reply_time: this.replyTime || 'in_a_few_minutes',
            continuity_via_email: this.continuityViaEmail,
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }

        await this.$store.dispatch('inboxes/updateInbox', payload);

        await this.$store.dispatch('inboxes/get');

        const labelList = this.inbox.label_list || this.inbox.labelList || [];
        this.selectedLabels = Array.isArray(labelList) ? labelList : [];
        this.selectedLabel =
          this.selectedLabels.length > 0 ? this.selectedLabels[0] : '';
        this.selectedLabel =
          this.selectedLabels.length > 0 ? this.selectedLabels[0] : '';
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch(
          'inboxes/deleteInboxAvatar',
          this.currentInboxId
        );
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(this.$t('INBOX_MGMT.DELETE.API.AVATAR_SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('INBOX_MGMT.DELETE.API.AVATAR_ERROR_MESSAGE')
        );
      }
    },
    toggleSenderNameType(key) {
      this.senderNameType = key;
    },
    onClickShowBusinessNameInput() {
      this.showBusinessNameInput = !this.showBusinessNameInput;
      if (this.showBusinessNameInput) {
        this.$nextTick(() => {
          this.$refs.businessNameInput.focus();
        });
      }
    },
    handleEnableAutoTicketCreation() {
      if (!this.enableAutoTicketCreation) {
        this.selectedTeamId = null;
        this.selectedBoardId = null;
        this.teamValidationError = false;
      }
      this.updateInbox();
    },
    async syncBoardForTeam(teamId) {
      if (!teamId) {
        this.selectedBoardId = null;
        return;
      }

      try {
        await this.$store.dispatch('boards/getByTeam', teamId);
        const teamBoard = this.$store.getters['boards/getBoard'];

        if (teamBoard) {
          this.selectedBoardId = teamBoard.id;
        } else {
          this.selectedBoardId = null;
        }
      } catch (error) {
        // Ignored. Assuming no board exists
        this.selectedBoardId = null;
      }
    },
    async onTeamChange(teamId) {
      this.selectedTeamId = teamId ? Number(teamId) : null;
      this.teamValidationError = false;
      await this.syncBoardForTeam(this.selectedTeamId);
    },
  },
  validations: {
    webhookUrl: {
      shouldBeUrl,
    },
    selectedInboxName: {},
  },
};
</script>

<template>
  <div
    class="overflow-auto flex-grow flex-shrink pr-0 pl-0 w-full min-w-0 settings"
  >
    <SettingIntroBanner
      :header-image="inbox.avatarUrl"
      :header-title="inboxName"
    >
      <woot-tabs
        class="[&_ul]:p-0"
        :index="selectedTabIndex"
        :border="false"
        @change="onTabChange"
      >
        <woot-tabs-item
          v-for="(tab, index) in tabs"
          :key="tab.key"
          :index="index"
          :name="tab.name"
          :show-badge="false"
          is-compact
        />
      </woot-tabs>
    </SettingIntroBanner>
    <section class="mx-auto w-full max-w-6xl">
      <MicrosoftReauthorize v-if="microsoftUnauthorized" :inbox="inbox" />
      <FacebookReauthorize v-if="facebookUnauthorized" :inbox="inbox" />
      <GoogleReauthorize v-if="googleUnauthorized" :inbox="inbox" />
      <InstagramReauthorize v-if="instagramUnauthorized" :inbox="inbox" />
      <TiktokReauthorize v-if="tiktokUnauthorized" :inbox="inbox" />
      <WhatsappReauthorize
        v-if="whatsappUnauthorized"
        :whatsapp-registration-incomplete="whatsappRegistrationIncomplete"
        :inbox="inbox"
      />
      <DuplicateInboxBanner
        v-if="hasDuplicateInstagramInbox"
        :content="$t('INBOX_MGMT.ADD.INSTAGRAM.DUPLICATE_INBOX_BANNER')"
        class="mx-8 mt-5"
      />
      <div v-if="selectedTabKey === 'inbox-settings'" class="mx-8">
        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_UPDATE_TITLE')"
          :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_UPDATE_SUB_TEXT')"
          :show-border="false"
        >
          <div class="flex flex-col gap-1 items-start mb-4">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_AVATAR.LABEL') }}
            </label>
            <Avatar
              :src="avatarUrl"
              :size="72"
              :icon-name="inboxIcon"
              name=""
              allow-upload
              rounded-full
              @upload="handleImageUpload"
              @delete="handleAvatarDelete"
            />
          </div>

          <div class="pb-4">
            <label
              class="pb-1 block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              {{ $t('INBOX_MGMT.LABELS.TITLE') || 'Labels' }}
            </label>

            <InboxLabelSelector
              :selected-label="selectedLabel"
              @update="onLabelUpdate"
            />
          </div>

          <woot-input
            v-model="selectedInboxName"
            class="pb-4"
            :class="{ error: v$.selectedInboxName.$error }"
            :label="inboxNameLabel"
            :placeholder="inboxNamePlaceHolder"
            :error="
              v$.selectedInboxName.$error
                ? $t('INBOX_MGMT.ADD.CHANNEL_NAME.ERROR')
                : ''
            "
            @blur="v$.selectedInboxName.$touch"
          />
          <woot-input
            v-if="isAPIInbox"
            v-model="webhookUrl"
            class="pb-4"
            :class="{ error: v$.webhookUrl.$error }"
            :label="
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.LABEL')
            "
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.PLACEHOLDER'
              )
            "
            :error="
              v$.webhookUrl.$error
                ? $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.ERROR')
                : ''
            "
            @blur="v$.webhookUrl.$touch"
          />
          <woot-input
            v-if="isAWebWidgetInbox"
            v-model="channelWebsiteUrl"
            class="pb-4"
            :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.LABEL')"
            :placeholder="
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.PLACEHOLDER')
            "
          />
          <woot-input
            v-if="isAWebWidgetInbox"
            v-model="channelWelcomeTitle"
            class="pb-4"
            :label="
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.LABEL')
            "
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.PLACEHOLDER'
              )
            "
          />

          <Editor
            v-if="isAWebWidgetInbox"
            v-model="channelWelcomeTagline"
            class="mb-4"
            :label="
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.LABEL')
            "
            :placeholder="
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.PLACEHOLDER'
              )
            "
            :max-length="255"
            channel-type="Context::InboxSettings"
          />

          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WIDGET_COLOR.LABEL') }}
            <woot-color-picker v-model="inbox.widget_color" />
          </label>

          <label v-if="isAWhatsAppChannel" class="pb-4">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.LABEL') }}
            <input v-model="whatsAppAPIProviderName" type="text" disabled />
          </label>

          <label class="pb-4">
            {{
              $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL')
            }}
            <select v-model="greetingEnabled">
              <option :value="true">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.ENABLED'
                  )
                }}
              </option>
              <option :value="false">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.DISABLED'
                  )
                }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.HELP_TEXT'
                )
              }}
            </p>
          </label>
          <div v-if="greetingEnabled" class="pb-4">
            <GreetingsEditor
              v-model="greetingMessage"
              :label="
                $t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL'
                )
              "
              :placeholder="
                $t(
                  'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
                )
              "
              :richtext="!textAreaChannels"
            />
          </div>
          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.TITLE') }}
            <select v-model="replyTime">
              <option key="in_a_few_minutes" value="in_a_few_minutes">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_FEW_MINUTES'
                  )
                }}
              </option>
              <option key="in_a_few_hours" value="in_a_few_hours">
                {{
                  $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_FEW_HOURS')
                }}
              </option>
              <option key="in_a_day" value="in_a_day">
                {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_DAY') }}
              </option>
            </select>

            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.HELP_TEXT') }}
            </p>
          </label>

          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_EMAIL_COLLECT_BOX') }}
            <select v-model="emailCollectEnabled">
              <option :value="true">
                {{ $t('INBOX_MGMT.EDIT.EMAIL_COLLECT_BOX.ENABLED') }}
              </option>
              <option :value="false">
                {{ $t('INBOX_MGMT.EDIT.EMAIL_COLLECT_BOX.DISABLED') }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.ENABLE_EMAIL_COLLECT_BOX_SUB_TEXT'
                )
              }}
            </p>
          </label>

          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MESSAGES_AFTER_RESOLVED') }}
            <select v-model="allowMessagesAfterResolved">
              <option :value="true">
                {{
                  $t('INBOX_MGMT.EDIT.ALLOW_MESSAGES_AFTER_RESOLVED.ENABLED')
                }}
              </option>
              <option :value="false">
                {{
                  $t('INBOX_MGMT.EDIT.ALLOW_MESSAGES_AFTER_RESOLVED.DISABLED')
                }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.ALLOW_MESSAGES_AFTER_RESOLVED_SUB_TEXT'
                )
              }}
            </p>
          </label>

          <label v-if="isAWebWidgetInbox" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL') }}
            <select v-model="continuityViaEmail">
              <option :value="true">
                {{ $t('INBOX_MGMT.EDIT.ENABLE_CONTINUITY_VIA_EMAIL.ENABLED') }}
              </option>
              <option :value="false">
                {{ $t('INBOX_MGMT.EDIT.ENABLE_CONTINUITY_VIA_EMAIL.DISABLED') }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL_SUB_TEXT'
                )
              }}
            </p>
          </label>
          <div v-if="!isAVoiceChannel" class="pb-4">
            <label>
              {{ $t('INBOX_MGMT.HELP_CENTER.LABEL') }}
            </label>
            <select v-model="selectedPortalSlug" class="filter__question">
              <option value="">
                {{ $t('INBOX_MGMT.HELP_CENTER.PLACEHOLDER') }}
              </option>
              <option v-for="p in portals" :key="p.slug" :value="p.slug">
                {{ p.name }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{ $t('INBOX_MGMT.HELP_CENTER.SUB_TEXT') }}
            </p>
          </div>
          <div v-if="isAnEmailChannel" class="pb-4">
            <label class="flex items-center gap-2 pb-2">
              <input
                id="enableAutoTicketCreation"
                v-model="enableAutoTicketCreation"
                type="checkbox"
                @change="handleEnableAutoTicketCreation"
              />
              <span
                class="text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                {{ $t('INBOX_MGMT.EDIT.AUTO_TICKET_CREATION.ENABLE_LABEL') }}
              </span>
            </label>

            <div v-if="enableAutoTicketCreation" class="mt-4 space-y-4">
              <div>
                <label
                  class="block pb-1 text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  {{ $t('INBOX_MGMT.EDIT.AUTO_TICKET_CREATION.TEAM_LABEL') }}
                  <span class="text-red-500">*</span>
                </label>
                <select
                  v-model="selectedTeamId"
                  :class="{ error: teamValidationError }"
                  @change="onTeamChange($event.target.value)"
                >
                  <option :value="null">
                    {{ $t('INBOX_MGMT.EDIT.AUTO_TICKET_CREATION.SELECT_TEAM') }}
                  </option>
                  <option v-for="team in teams" :key="team.id" :value="team.id">
                    {{ team.name }}
                  </option>
                </select>
                <p
                  v-if="teamValidationError"
                  class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                  {{
                    $t('INBOX_MGMT.EDIT.AUTO_TICKET_CREATION.TEAM_VALIDATION')
                  }}
                </p>
                <p v-else class="mt-1 text-sm text-n-slate-11">
                  {{
                    $t('INBOX_MGMT.EDIT.AUTO_TICKET_CREATION.TEAM_HELP_TEXT')
                  }}
                </p>
              </div>

              <div v-if="selectedBoard">
                <label
                  class="block pb-1 text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  {{ $t('INBOX_MGMT.EDIT.AUTO_TICKET_CREATION.BOARD_LABEL') }}
                  <span class="text-red-500">*</span>
                </label>
                <div
                  class="w-full px-3 py-2 text-sm border border-blue-500 dark:border-blue-400 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                >
                  {{ selectedBoard.name }}
                </div>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{
                    $t('INBOX_MGMT.EDIT.AUTO_TICKET_CREATION.BOARD_HELP_TEXT')
                  }}
                </p>
              </div>

              <div
                v-if="autoTicketSummary"
                class="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
              >
                <svg
                  class="flex-shrink-0 w-5 h-5 mt-0.5 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <p class="text-sm text-blue-900 dark:text-blue-100">
                  {{ autoTicketSummary }}
                </p>
              </div>
            </div>
          </div>
          <label v-if="canLocktoSingleConversation" class="pb-4">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.LOCK_TO_SINGLE_CONVERSATION') }}
            <select v-model="locktoSingleConversation">
              <option :value="true">
                {{ $t('INBOX_MGMT.EDIT.LOCK_TO_SINGLE_CONVERSATION.ENABLED') }}
              </option>
              <option :value="false">
                {{ $t('INBOX_MGMT.EDIT.LOCK_TO_SINGLE_CONVERSATION.DISABLED') }}
              </option>
            </select>
            <p class="pb-1 text-sm not-italic text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.LOCK_TO_SINGLE_CONVERSATION_SUB_TEXT'
                )
              }}
            </p>
          </label>

          <label v-if="isAWebWidgetInbox">
            {{ $t('INBOX_MGMT.FEATURES.LABEL') }}
          </label>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pt-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="attachments"
              @input="handleFeatureFlag"
            />
            <label for="attachments">
              {{ $t('INBOX_MGMT.FEATURES.DISPLAY_FILE_PICKER') }}
            </label>
          </div>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="emoji_picker"
              @input="handleFeatureFlag"
            />
            <label for="emoji_picker">
              {{ $t('INBOX_MGMT.FEATURES.DISPLAY_EMOJI_PICKER') }}
            </label>
          </div>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="end_conversation"
              @input="handleFeatureFlag"
            />
            <label for="end_conversation">
              {{ $t('INBOX_MGMT.FEATURES.ALLOW_END_CONVERSATION') }}
            </label>
          </div>
          <div v-if="isAWebWidgetInbox" class="flex gap-2 pb-4">
            <input
              v-model="selectedFeatureFlags"
              type="checkbox"
              value="use_inbox_avatar_for_bot"
              @input="handleFeatureFlag"
            />
            <label for="use_inbox_avatar_for_bot">
              {{ $t('INBOX_MGMT.FEATURES.USE_INBOX_AVATAR_FOR_BOT') }}
            </label>
          </div>
        </SettingsSection>
        <SettingsSection
          v-if="isAWebWidgetInbox || isAnEmailChannel"
          :title="$t('INBOX_MGMT.EDIT.SENDER_NAME_SECTION.TITLE')"
          :sub-title="$t('INBOX_MGMT.EDIT.SENDER_NAME_SECTION.SUB_TEXT')"
          :show-border="false"
        >
          <div class="pb-4">
            <SenderNameExamplePreview
              :sender-name-type="senderNameType"
              :business-name="businessName"
              @update="toggleSenderNameType"
            />
            <div class="flex flex-col gap-2 items-start mt-2">
              <NextButton
                ghost
                blue
                :label="
                  $t(
                    'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.BUTTON_TEXT'
                  )
                "
                @click="onClickShowBusinessNameInput"
              />
              <div v-if="showBusinessNameInput" class="flex gap-2 w-[80%]">
                <input
                  ref="businessNameInput"
                  v-model="businessName"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.PLACEHOLDER'
                    )
                  "
                  class="mb-0"
                  type="text"
                />
                <NextButton
                  :label="
                    $t(
                      'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.SAVE_BUTTON_TEXT'
                    )
                  "
                  class="flex-shrink-0"
                  @click="updateInbox"
                />
              </div>
            </div>
          </div>
        </SettingsSection>
        <SettingsSection :show-border="false">
          <NextButton
            v-if="isAPIInbox"
            type="submit"
            :disabled="v$.webhookUrl.$invalid"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="uiFlags.isUpdating"
            @click="updateInbox"
          />
          <NextButton
            v-else
            type="submit"
            :disabled="v$.$invalid"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="uiFlags.isUpdating"
            @click="updateInbox"
          />
        </SettingsSection>
      </div>

      <div v-if="selectedTabKey === 'collaborators'" class="mx-8">
        <CollaboratorsPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'auto-messages'" class="mx-8">
        <SettingsSection
          :title="
            $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_MESSAGE_ON_CLASSIFICATION')
          "
          :sub-title="
            $t(
              'INBOX_MGMT.SETTINGS_POPUP.AUTO_MESSAGE_ON_CLASSIFICATION_SUB_TEXT'
            )
          "
          :show-border="false"
        >
          <div class="flex flex-col gap-4 pb-4">
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-2">
                <input
                  id="autoHideCommentOnNegative"
                  v-model="autoHideCommentOnNegative"
                  type="checkbox"
                  class="h-4 w-4 rounded border-n-weak text-primary-600 focus:ring-primary-500"
                />
                <label for="autoHideCommentOnNegative" class="text-sm">
                  {{
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.AUTO_HIDE_COMMENT_ON_NEGATIVE'
                    )
                  }}
                </label>
              </div>
              <p class="text-xs text-n-slate-11">
                {{
                  $t(
                    'INBOX_MGMT.SETTINGS_POPUP.AUTO_HIDE_COMMENT_ON_NEGATIVE_HELP'
                  )
                }}
              </p>
            </div>
            <div class="border-t border-n-weak" />
            <div class="flex flex-col gap-2">
              <div class="flex flex-wrap items-center gap-2">
                <input
                  id="enableAutoPositiveAutomation"
                  v-model="enableAutoPositiveAutomation"
                  type="checkbox"
                  class="h-4 w-4 shrink-0 rounded border-n-weak text-primary-600 focus:ring-primary-500"
                />
                <label
                  class="text-sm text-n-slate-12"
                  for="enableAutoPositiveAutomation"
                >
                  {{
                    $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_SENTIMENT_CALL_DIRECT')
                  }}
                </label>
                <span
                  class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold bg-black-100 dark:bg-slate-800"
                >
                  {{
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.AUTO_SENTIMENT_POSITIVE_BADGE'
                    )
                  }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_POSITIVE_SECTION_HELP') }}
              </p>
              <div
                v-show="enableAutoPositiveAutomation"
                class="flex flex-col gap-1 border-l-2 border-n-weak pl-4"
              >
                <label
                  class="text-sm font-medium text-n-slate-12"
                  for="autoDmMessagePositive"
                >
                  {{
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.AUTO_DM_MESSAGE_POSITIVE_LABEL'
                    )
                  }}
                </label>
                <textarea
                  id="autoDmMessagePositive"
                  v-model="autoDmMessagePositive"
                  rows="4"
                  class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.AUTO_DM_MESSAGE_POSITIVE_PLACEHOLDER'
                    )
                  "
                />
              </div>
            </div>
            <div class="border-t border-n-weak" />
            <div class="flex flex-col gap-2">
              <div class="flex flex-wrap items-center gap-2">
                <input
                  id="enableNegativeAutomation"
                  v-model="enableNegativeAutomation"
                  type="checkbox"
                  class="h-4 w-4 shrink-0 rounded border-n-weak text-primary-600 focus:ring-primary-500"
                />
                <label
                  class="text-sm text-n-slate-12"
                  for="enableNegativeAutomation"
                >
                  {{
                    $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_SENTIMENT_CALL_DIRECT')
                  }}
                </label>
                <span
                  class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold bg-black-100 dark:bg-slate-800"
                >
                  {{
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.AUTO_SENTIMENT_NEGATIVE_BADGE'
                    )
                  }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_NEGATIVE_SECTION_HELP') }}
              </p>
              <div
                v-show="enableNegativeAutomation"
                class="flex flex-col gap-4 border-l-2 border-n-weak pl-4"
              >
                <div class="flex flex-col gap-1">
                  <label
                    class="text-sm font-medium text-n-slate-12"
                    for="autoDmMessageNegative"
                  >
                    {{
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.AUTO_DM_MESSAGE_NEGATIVE_LABEL'
                      )
                    }}
                  </label>
                  <textarea
                    id="autoDmMessageNegative"
                    v-model="autoDmMessageNegative"
                    rows="4"
                    class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    :placeholder="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.AUTO_DM_MESSAGE_NEGATIVE_PLACEHOLDER'
                      )
                    "
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label
                    class="text-sm font-medium text-n-slate-12"
                    for="autoCommentMessageNegative"
                  >
                    {{
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.AUTO_COMMENT_MESSAGE_NEGATIVE_LABEL'
                      )
                    }}
                  </label>
                  <textarea
                    id="autoCommentMessageNegative"
                    v-model="autoCommentMessageNegative"
                    rows="4"
                    class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    :placeholder="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.AUTO_COMMENT_MESSAGE_NEGATIVE_PLACEHOLDER'
                      )
                    "
                  />
                  <p class="text-xs text-n-slate-11">
                    {{
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.AUTO_COMMENT_MESSAGE_NEGATIVE_HELP'
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="pt-2">
            <NextButton
              type="button"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="uiFlags.isUpdating"
              @click="updateInbox"
            />
          </div>
        </SettingsSection>
      </div>
      <div v-if="selectedTabKey === 'configuration'">
        <ConfigurationPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'csat'">
        <CustomerSatisfactionPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'pre-chat-form'">
        <PreChatFormSettings :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'business-hours'">
        <WeeklyAvailability :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'workflows'">
        <WorkflowsPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'widget-builder'">
        <WidgetBuilder :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'bot-configuration'">
        <BotConfiguration :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'whatsapp-health'">
        <AccountHealth :health-data="healthData" />
      </div>
    </section>
  </div>
</template>
